import { useState, useEffect } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { API, generateBarcode } from "./constants";
import { GeneralTab, PricingTab, StockTab, HistoryTab, OtherTab, ProductInfoTab } from "./tabs";
import LocationsStockTab from "./LocationsStockTab";

const normalizeLookupValue = (value) => (value || "").trim().toLowerCase();

const _blank = (v) => v === null || v === undefined || v === "";
const _same = (a, b) => {
  if (_blank(a) && _blank(b)) return true;
  if (typeof a === "object" || typeof b === "object") return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  const na = Number(a), nb = Number(b);
  if (!_blank(a) && !_blank(b) && !Number.isNaN(na) && !Number.isNaN(nb)) return Math.abs(na - nb) < 1e-9;
  return String(a) === String(b);
};
const changedFieldsOnly = (original, edited) => {
  const out = { id: original.id };
  Object.keys(edited).forEach((k) => {
    if (k !== "id" && !_same(original[k], edited[k])) out[k] = edited[k];
  });
  return out;
};

export const ProductDetailModal = ({ 
  open, 
  onClose, 
  product, 
  onSave, 
  categories, 
  departments, 
  onRefreshLists, 
  onRefreshProducts, 
  isNew = false 
}) => {
  const [formData, setFormData] = useState(product);
  const [newCategory, setNewCategory] = useState("");
  const [newDepartment, setNewDepartment] = useState("");
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const [calculationPreview, setCalculationPreview] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [autoGenerateBarcode, setAutoGenerateBarcode] = useState(false);
  const [historyStats, setHistoryStats] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  
  const [initialExpiryBatch, setInitialExpiryBatch] = useState({
    enabled: false,
    expiry_date: "",
    quantity: "",
    batch_number: ""
  });
  const [existingBatches, setExistingBatches] = useState([]);
  const [creatingBatch, setCreatingBatch] = useState(false);
  const [suppliersList, setSuppliersList] = useState([]);
  const [warehouseEnabled, setWarehouseEnabled] = useState(false);

  // Check whether the warehouse module is on so we can show the Locations tab
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/locations/module/status`);
        if (!cancelled) setWarehouseEnabled(!!r.data.enabled);
      } catch (_) { if (!cancelled) setWarehouseEnabled(false); }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Fetch suppliers list
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await axios.get(`${API}/suppliers`);
        // Sort suppliers alphabetically by name
        const sortedSuppliers = (response.data || []).sort((a, b) => 
          (a.name || '').localeCompare(b.name || '')
        );
        setSuppliersList(sortedSuppliers);
      } catch (error) {
        console.error("Error fetching suppliers:", error);
      }
    };
    if (open) {
      fetchSuppliers();
    }
  }, [open]);

  const [generatingPlu, setGeneratingPlu] = useState(false);

  // Auto-generate PLU code for new products
  const generatePluCode = async () => {
    setGeneratingPlu(true);
    try {
      const response = await axios.get(`${API}/products/next-plu`, {
        timeout: 5000 // 5 second timeout
      });
      if (response.data?.next_plu) {
        setFormData(prev => ({ ...prev, plu_code: response.data.next_plu }));
        toast.success(`PLU code generated: ${response.data.next_plu}`);
      } else {
        // Fallback to timestamp-based PLU
        const fallbackPlu = Date.now().toString().slice(-6);
        setFormData(prev => ({ ...prev, plu_code: fallbackPlu }));
        toast.success(`PLU code generated: ${fallbackPlu}`);
      }
    } catch (error) {
      console.error("Error generating PLU:", error);
      // Fallback to timestamp-based PLU when network fails
      const fallbackPlu = Date.now().toString().slice(-6);
      setFormData(prev => ({ ...prev, plu_code: fallbackPlu }));
      toast.success(`PLU code generated (offline): ${fallbackPlu}`);
    } finally {
      setGeneratingPlu(false);
    }
  };

  useEffect(() => {
    // For new products, ensure we start with a clean slate
    if (!product?.id) {
      // Apply locked field values for new products
      const locks = (() => { try { return JSON.parse(localStorage.getItem("product_field_locks") || "{}"); } catch { return {}; } })();
      
      setFormData({
        ...product,
        barcodes: [],
        plu_code: "",
        barcode: "",
        name: "",
        description: "",
        category: locks.category || "",
        department: locks.department || "",
        product_group: locks.product_group || "",
        supplier: locks.supplier || "",
        supplier_plu: "",
      });
      setCalculationPreview(null);
      setHistoryStats(null);
      setAutoGenerateBarcode(false); // Default to manual barcode entry
      setInitialExpiryBatch({
        enabled: false,
        expiry_date: "",
        quantity: "",
        batch_number: ""
      });
      setSelectedCategoryId(null);
      setExistingBatches([]);
      
      // If category is locked, find and set its ID for department filtering
      if (locks.category) {
        const cat = categories.find(c => normalizeLookupValue(c.name) === normalizeLookupValue(locks.category));
        if (cat) setSelectedCategoryId(cat.id);
      }
      
      // Don't auto-generate PLU on modal open - let user click the button
      // This avoids network errors in slow/unstable connections
      
      return;
    }
    
    // For existing products
    const productWithBarcodes = {
      ...product,
      barcodes: product?.barcodes?.length > 0 
        ? product.barcodes 
        : (product?.barcode ? [product.barcode] : [])
    };
    setFormData(productWithBarcodes);
    setCalculationPreview(null);
    setHistoryStats(null);
    setAutoGenerateBarcode(!product?.barcode && (!product?.barcodes || product.barcodes.length === 0));
    
    // Load linked product name for case products
    if (product?.is_case_product && product?.linked_product_id && !product?.linked_product_name) {
      axios.get(`${API}/products/search?q=${product.linked_product_id}`).then(res => {
        const items = res.data?.products || res.data || [];
        const linked = items.find(p => p.id === product.linked_product_id);
        if (linked) {
          setFormData(prev => ({ ...prev, linked_product_name: linked.name }));
        }
      }).catch(() => {});
    }
    
    setInitialExpiryBatch({
      enabled: false,
      expiry_date: "",
      quantity: product?.current_stock > 0 ? String(product.current_stock) : "",
      batch_number: ""
    });
    
    if (product?.category) {
      const cat = categories.find(
        (c) => normalizeLookupValue(c.name) === normalizeLookupValue(product.category)
      );
      setSelectedCategoryId(cat?.id || null);
    } else {
      setSelectedCategoryId(null);
    }
    
    fetchHistoryStats(product.id);
    if (product?.track_expiry) {
      fetchExistingBatches(product.id);
    }
  }, [product, categories]);
  
  useEffect(() => {
    if (selectedCategoryId) {
      const filtered = departments.filter(d => d.category_id === selectedCategoryId);
      const legacy = departments.filter(
        (d) => !d.category_id && normalizeLookupValue(d.name) === normalizeLookupValue(formData?.department)
      );
      setFilteredDepartments([...filtered, ...legacy]);
    } else {
      setFilteredDepartments(departments);
    }
  }, [selectedCategoryId, departments, formData?.department]);
  
  const fetchExistingBatches = async (productId) => {
    try {
      const response = await axios.get(`${API}/expiry/product/${productId}/batches`);
      setExistingBatches(response.data.batches || []);
    } catch (error) {
      console.error("Error fetching existing batches:", error);
      setExistingBatches([]);
    }
  };
  
  const handleCreateInitialBatch = async () => {
    if (!initialExpiryBatch.expiry_date) {
      toast.error("Please enter an expiry date");
      return;
    }
    if (!initialExpiryBatch.quantity || parseFloat(initialExpiryBatch.quantity) <= 0) {
      toast.error("Please enter a valid quantity");
      return;
    }
    
    setCreatingBatch(true);
    try {
      await saveWithRetry(() => axios.post(`${API}/expiry/batches`, {
        product_id: product.id,
        expiry_date: initialExpiryBatch.expiry_date,
        quantity_received: parseFloat(initialExpiryBatch.quantity),
        batch_number: initialExpiryBatch.batch_number || null,
        supplier_name: "Initial Stock Entry"
      }));
      
      toast.success("Expiry batch created for existing stock!");
      setInitialExpiryBatch({
        enabled: false,
        expiry_date: "",
        quantity: "",
        batch_number: ""
      });
      fetchExistingBatches(product.id);
    } catch (error) {
      console.error("Error creating initial batch:", error);
      toast.error(errorMessage(error, "Failed to create expiry batch"));
    } finally {
      setCreatingBatch(false);
    }
  };
  
  const handleCategoryChange = (categoryName) => {
    const cat = categories.find(
      (c) => normalizeLookupValue(c.name) === normalizeLookupValue(categoryName)
    );
    setSelectedCategoryId(cat?.id || null);
    
    if (cat?.id && formData.department) {
      const deptBelongsToCategory = departments.some(
        (d) => normalizeLookupValue(d.name) === normalizeLookupValue(formData.department) && d.category_id === cat.id
      );
      if (!deptBelongsToCategory) {
        setFormData(prev => ({ ...prev, category: categoryName, department: null }));
        return;
      }
    }
    
    setFormData(prev => ({ ...prev, category: categoryName }));
  };

  const fetchHistoryStats = async (productId) => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/products/${productId}/history-stats`);
      setHistoryStats(response.data);
    } catch (error) {
      console.error("Error fetching history stats:", error);
      setHistoryStats(null);
      if (error.response?.status !== 404) {
        const detail = error.response?.data?.detail
          || (typeof error.response?.data === 'string' ? error.response.data.substring(0, 120) : null)
          || error.message
          || "Unknown error";
        toast.error(`Product history failed: ${detail}`);
      }
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCalculatePreview = async () => {
    setCalculating(true);
    try {
      const response = await axios.get(`${API}/products/${product.id}/calculate-stock-levels`);
      setCalculationPreview(response.data);
      
      // Check if we have sales data (avg_monthly_sales > 0 means there's sales history)
      const hasSalesData = response.data?.calculation?.avg_monthly_sales > 0;
      
      if (hasSalesData) {
        setFormData(prev => ({
          ...prev,
          minimum_level: response.data.suggested_levels?.minimum_level || prev.minimum_level,
          reorder_level: response.data.suggested_levels?.reorder_level || prev.reorder_level,
          maximum_level: response.data.suggested_levels?.maximum_level || prev.maximum_level
        }));
        toast.success("Stock levels calculated and populated!");
      } else {
        toast.warning("Not enough sales data for automatic calculation");
      }
    } catch (error) {
      console.error("Error calculating:", error);
      toast.error("Failed to calculate stock levels");
    } finally {
      setCalculating(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Direct order or skip_price_rounding items keep full cent precision; regular items round to 10c
      const isDirectOrder = field === 'direct_order' ? value : prev.direct_order;
      const skipRounding = field === 'skip_price_rounding' ? value : prev.skip_price_rounding;
      const roundPrice = (price) => (isDirectOrder || skipRounding)
        ? Math.round(price * 100) / 100 
        : Math.round(price * 10) / 10;
      
      const calculateSellingPrice = (cost, markup, vatCategory) => {
        if (!cost || cost <= 0) return 0;
        const costWithMarkup = cost * (1 + (markup || 0) / 100);
        const vatRate = vatCategory === "Zero-rated 0%" ? 0 : 0.15;
        return roundPrice(costWithMarkup * (1 + vatRate));
      };
      
      const calculateMarkup = (sellingPriceInclVat, cost, vatCategory) => {
        if (!cost || cost <= 0 || !sellingPriceInclVat || sellingPriceInclVat <= 0) return 0;
        const vatRate = vatCategory === "Zero-rated 0%" ? 0 : 0.15;
        const sellingPriceExclVat = sellingPriceInclVat / (1 + vatRate);
        return Math.max(0, Math.round(((sellingPriceExclVat - cost) / cost) * 100 * 100) / 100);
      };
      
      const calculateMaxDiscount = (markup) => {
        if (markup <= 20) return 5;
        if (markup <= 30) return 7.5;
        return 10;
      };
      
      // When markup is set from a price change, just store it without recalculating selling price
      if (field === 'markup_percent_from_price') {
        updated.markup_percent = value;
        // Only auto-suggest the max discount when the user hasn't set
        // one yet — otherwise typing "5" in Max Discount % Allowed
        // gets stomped the moment any other price/markup field blurs.
        const currentDisc = prev.discount_percent_allowed || 0;
        if (value > 0 && currentDisc === 0) {
          updated.discount_percent_allowed = calculateMaxDiscount(value);
        }
        return updated;
      }
      
      if (field === 'markup_percent') {
        if (value < 0) {
          toast.error("Markup % cannot be negative");
          return prev;
        }
        const cost = prev.average_cost_price_excl_vat || prev.last_cost_price_excl_vat || 0;
        if (cost > 0) {
          updated.selling_price_incl_vat = calculateSellingPrice(cost, value, prev.vat_category);
        }
        // Same guard — respect the user's manual Max Discount entry.
        const currentDisc = prev.discount_percent_allowed || 0;
        if (value > 0 && currentDisc === 0) {
          updated.discount_percent_allowed = calculateMaxDiscount(value);
        }
      }
      
      if (field === 'last_cost_price_excl_vat' || field === 'average_cost_price_excl_vat') {
        const cost = value;
        const markup = prev.markup_percent || 0;
        if (prev.price_locked) {
          if (cost > 0 && prev.selling_price_incl_vat > 0) {
            updated.markup_percent = calculateMarkup(prev.selling_price_incl_vat, cost, prev.vat_category);
          }
        } else if (cost > 0 && markup > 0) {
          updated.selling_price_incl_vat = calculateSellingPrice(cost, markup, prev.vat_category);
        }
        if (field === 'last_cost_price_excl_vat') {
          updated.average_cost_price_excl_vat = value;
        }
      }
      
      if (field === 'vat_category') {
        const cost = prev.average_cost_price_excl_vat || prev.last_cost_price_excl_vat || 0;
        const markup = prev.markup_percent || 0;
        if (cost > 0 && markup > 0 && !prev.price_locked) {
          updated.selling_price_incl_vat = calculateSellingPrice(cost, markup, value);
        }
      }
      
      if (field === 'selling_price_incl_vat') {
        const roundedPrice = (isDirectOrder || skipRounding) ? Math.round(value * 100) / 100 : Math.round(value * 10) / 10;
        updated.selling_price_incl_vat = roundedPrice;
        
        const cost = prev.average_cost_price_excl_vat || prev.last_cost_price_excl_vat || 0;
        if (cost > 0 && roundedPrice > 0) {
          const newMarkup = calculateMarkup(roundedPrice, cost, prev.vat_category);
          updated.markup_percent = newMarkup;
          // Same guard — a manual max-discount value must survive a
          // shelf-price change too.
          const currentDisc = prev.discount_percent_allowed || 0;
          if (newMarkup > 0 && currentDisc === 0) {
            updated.discount_percent_allowed = calculateMaxDiscount(newMarkup);
          }
        }
      }
      
      return updated;
    });
  };

  const handleSubmit = () => {
    let dataToSave = { ...formData };
    
    if (autoGenerateBarcode && (!formData.barcodes || formData.barcodes.length === 0)) {
      const generatedBarcode = generateBarcode();
      dataToSave.barcode = generatedBarcode;
      dataToSave.barcodes = [generatedBarcode];
    }
    
    if (dataToSave.barcodes) {
      dataToSave.barcodes = dataToSave.barcodes.filter(b => b && b.trim());
      if (dataToSave.barcodes.length > 0 && !dataToSave.barcode) {
        dataToSave.barcode = dataToSave.barcodes[0];
      }
    }
    
    delete dataToSave.stock_on_order;

    // Existing product: send only what was changed on this screen, so a screen opened
    // before a GRV can't write back old prices/stock.
    if (product?.id) dataToSave = changedFieldsOnly(product, dataToSave);

    onSave(dataToSave);
  };

  const handleAddCategory = async () => {
    if (!newCategory) {
      toast.error("Please enter a category name");
      return;
    }
    try {
      await axios.post(`${API}/categories-departments`, {
        name: newCategory,
        type: "category"
      });
      toast.success("Category added");
      setNewCategory("");
      setShowAddCategory(false);
      onRefreshLists();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 
                  Array.isArray(detail) ? detail.map(e => e.msg).join(', ') :
                  "Failed to add category";
      toast.error(msg);
    }
  };

  const handleAddDepartment = async () => {
    if (!newDepartment) {
      toast.error("Please enter a department name");
      return;
    }
    if (!selectedCategoryId) {
      toast.error("Please select a category first");
      return;
    }
    try {
      await axios.post(`${API}/categories-departments`, {
        name: newDepartment,
        type: "department",
        category_id: selectedCategoryId
      });
      toast.success(`Department "${newDepartment}" added to ${formData.category}`);
      setNewDepartment("");
      setShowAddDepartment(false);
      onRefreshLists();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 
                  Array.isArray(detail) ? detail.map(e => e.msg).join(', ') :
                  "Failed to add department";
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            {isNew ? (
              <>
                <Plus className="h-6 w-6 text-green-600" />
                Add New Product
              </>
            ) : (
              <>Product Master - {formData.name}</>
            )}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="general" className="w-full">
          <TabsList className={`grid w-full ${warehouseEnabled ? 'grid-cols-7' : 'grid-cols-6'}`}>
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="pricing">Pricing & VAT</TabsTrigger>
            <TabsTrigger value="stock">Stock & Location</TabsTrigger>
            {warehouseEnabled && (
              <TabsTrigger value="locations" disabled={isNew} data-testid="locations-tab-trigger">Locations</TabsTrigger>
            )}
            <TabsTrigger value="product-info" disabled={isNew}>Product Info</TabsTrigger>
            <TabsTrigger value="history" disabled={isNew}>History & Stats</TabsTrigger>
            <TabsTrigger value="other">Other</TabsTrigger>
          </TabsList>

          <TabsContent value="general">
            <GeneralTab
              formData={formData}
              handleChange={handleChange}
              categories={categories}
              departments={departments}
              suppliersList={suppliersList}
              isNew={isNew}
              autoGenerateBarcode={autoGenerateBarcode}
              setAutoGenerateBarcode={setAutoGenerateBarcode}
              handleCategoryChange={handleCategoryChange}
              selectedCategoryId={selectedCategoryId}
              filteredDepartments={filteredDepartments}
              showAddCategory={showAddCategory}
              setShowAddCategory={setShowAddCategory}
              showAddDepartment={showAddDepartment}
              setShowAddDepartment={setShowAddDepartment}
              newCategory={newCategory}
              setNewCategory={setNewCategory}
              newDepartment={newDepartment}
              setNewDepartment={setNewDepartment}
              handleAddCategory={handleAddCategory}
              handleAddDepartment={handleAddDepartment}
              onGeneratePlu={generatePluCode}
              generatingPlu={generatingPlu}
            />
          </TabsContent>

          <TabsContent value="pricing">
            <PricingTab
              formData={formData}
              handleChange={handleChange}
              suppliersList={suppliersList}
            />
          </TabsContent>

          <TabsContent value="stock">
            <StockTab
              formData={formData}
              handleChange={handleChange}
              isNew={isNew}
              calculationPreview={calculationPreview}
              calculating={calculating}
              handleCalculatePreview={handleCalculatePreview}
              existingBatches={existingBatches}
              initialExpiryBatch={initialExpiryBatch}
              setInitialExpiryBatch={setInitialExpiryBatch}
              creatingBatch={creatingBatch}
              handleCreateInitialBatch={handleCreateInitialBatch}
            />
          </TabsContent>

          {warehouseEnabled && (
            <TabsContent value="locations">
              <LocationsStockTab productId={product?.id} productName={formData?.name} />
            </TabsContent>
          )}

          <TabsContent value="product-info">
            <ProductInfoTab
              formData={formData}
              API={API}
            />
          </TabsContent>

          <TabsContent value="history">
            <HistoryTab
              historyStats={historyStats}
              loadingHistory={loadingHistory}
              fetchHistoryStats={fetchHistoryStats}
              productId={product?.id}
            />
          </TabsContent>

          <TabsContent value="other">
            <OtherTab
              formData={formData}
              handleChange={handleChange}
            />
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            className={isNew ? "bg-green-600 hover:bg-green-700" : "bg-purple-600 hover:bg-purple-700"}
          >
            {isNew ? "Create Product" : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
